import {
  chatBotPath as defaultChatBotPath,
  chatInboxPath as defaultChatInboxPath,
  chatRoomPath as defaultChatRoomPath,
  chatRoomsPath as defaultChatRoomsPath,
  newChatRoomPath as defaultNewChatRoomPath,
} from "@circle-react/helpers/urlHelpers";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { useRouter, useLocation } from "@tanstack/react-router";
import { useMemo } from "react";

import { aiInboxSearchSchema } from "@/react/pages/UnifiedInbox/AiInbox/aiInboxSearchParamsSchema";

export const UNIFIED_INBOX_BASE_PATH = settingsNavPathV3.unified_inbox.index;
export const UNIFIED_INBOX_MESSAGES_ROOT =
  `${UNIFIED_INBOX_BASE_PATH}/messages` as const;
export const UNIFIED_INBOX_CHAT_THREADS_ROOT =
  `${UNIFIED_INBOX_BASE_PATH}/chat-threads` as const;

// TODO: The string return types here prevent TSR from validating paths against the route tree
export interface MessagingPaths {
  aiInboxPath: () => string;
  chatBotPath: (args: { id: string }) => string;
  chatInboxPath: (args: { id: string }) => string;
  chatRoomPath: (args: { uuid: string; messageHashId?: string }) => string;
  chatRoomsPath: () => string;
  isBotPath: (path: string) => boolean;
  isRoomsIndexPath: (path: string) => boolean;
  newChatRoomPath: () => string;
}

export const useMessagingPaths = (): MessagingPaths => {
  const pathname = useLocation({ select: location => location.pathname });
  const router = useRouter();

  return useMemo((): MessagingPaths => {
    const isUnifiedInboxRoute = pathname.startsWith(UNIFIED_INBOX_BASE_PATH);

    if (isUnifiedInboxRoute) {
      const root = UNIFIED_INBOX_MESSAGES_ROOT;
      return {
        aiInboxPath: () => settingsNavPathV3.unified_inbox.ai,
        chatRoomsPath: () => root,
        chatRoomPath: ({ uuid, messageHashId }) => {
          const location = router.buildLocation({
            to: "/settings/inbox/messages/$uuid",
            params: { uuid },
            hash: messageHashId ? `message_${messageHashId}` : undefined,
          });
          return location.href;
        },
        newChatRoomPath: () => `${root}/new`,
        chatBotPath: ({ id }) => `${root}/bot/${id}`,
        chatInboxPath: ({ id }) =>
          router.buildLocation({
            to: "/settings/inbox/ai/conversations/$id",
            params: { id },
            search: prev => aiInboxSearchSchema.parse(prev),
          }).href,
        isBotPath: path => path.startsWith(`${root}/bot/`),
        isRoomsIndexPath: path => path === root,
      };
    }

    return {
      aiInboxPath: () => settingsNavPathV3.community_inbox.index,
      chatRoomsPath: defaultChatRoomsPath,
      chatRoomPath: defaultChatRoomPath,
      newChatRoomPath: defaultNewChatRoomPath,
      chatBotPath: defaultChatBotPath,
      chatInboxPath: defaultChatInboxPath,
      isBotPath: path => path.startsWith("/messages/bot"),
      isRoomsIndexPath: path => path === defaultChatRoomsPath(),
    };
  }, [pathname, router]);
};
